import Email from '@mui/icons-material/Email';
import PermIdentity from '@mui/icons-material/PermIdentity';
import { Alert } from "@mui/lab";
import { AppBar, Box, Card, CardContent, Dialog, DialogContent, FormControlLabel, FormGroup, FormHelperText, InputAdornment, List, ListItem, Switch, TextField, Toolbar } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import appMainTheme, { appThemeNames, appThemes } from "assets/appMainTheme";
import { AppImages } from "assets/appResources";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { ImageSelection } from "components/FileUpload/ImageSelection";
import FormSubtitle from "components/FormSubtitle";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldNumeric from "components/TextFieldNumeric";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { GetSetupAdminDto, SaveSetupAdminDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import Session from "services/Session";
import SetupAdminLogDialog from "views/System/Components/SetupAdminLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

export interface ISetupProps {
    onEditing: (editing: boolean) => void;
}

interface IProps {
}

interface IState {
    dialog: any,
    formCycle: RequestLifeCycle,
    lastError: any,
    record: Partial<GetSetupAdminDto>,
    validate: {
        name: boolean,
        minPasswordLen: boolean,
        maxAuditLogDays: boolean,
        thumbLogo: boolean,
        thumbBack: boolean,
    };
}

type Props = ISetupProps & IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupAdminEdit extends React.Component<Props, IState> {

    private compareDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            record: {
                background: null,
                enableAutoRegister: null,
                enablePasswordRecovery: null,
                logo: null,
                mainTheme: "default",
                maxAuditLogDays: null,
                maxInvalidPasswordAttempts: null,
                minPasswordLen: null,
                passwordAttemptWindow: null,
                passwordExcludeNameSurname: null,
                passwordHasOneNumber: null,
                passwordHasOneSymbol: null,
                passwordHasOneUpperLetter: null,
                recordInfo: null,
                requireAccountConfirmation: null,
                title: null,
                emailDefaultAddress: null,
                emailDefaultName: "Genie Mail"
            },
            validate: {
                name: null,
                minPasswordLen: null,
                maxAuditLogDays: null,
                thumbBack: true,
                thumbLogo: true,
            },
        };
    }


    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupAdminEdit." + r, f);
    }

    ///
    private load() {
        api.getSetupAdmin()
            .then((dto) => {
                this.compareDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto,
                    validate: {
                        name: true,
                        minPasswordLen: true,
                        maxAuditLogDays: true,
                        thumbBack: true,
                        thumbLogo: true,
                    },
                }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({
                    lastError: err,
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private getPostDto(): SaveSetupAdminDto {
        const { record } = this.state;

        return {
            mainTheme: record.mainTheme,
            maxAuditLogDays: record.maxAuditLogDays,
            maxInvalidPasswordAttempts: record.maxInvalidPasswordAttempts,
            minPasswordLen: record.minPasswordLen,
            passwordExcludeNameSurname: record.passwordExcludeNameSurname,
            passwordHasOneNumber: record.passwordHasOneNumber,
            passwordHasOneSymbol: record.passwordHasOneSymbol,
            passwordHasOneUpperLetter: record.passwordHasOneUpperLetter,
            passwordAttemptWindow: record.passwordAttemptWindow,

            enableAutoRegister: record.enableAutoRegister,
            enablePasswordRecovery: record.enablePasswordRecovery,
            requireAccountConfirmation: record.requireAccountConfirmation,
            corporateMode: record.corporateMode,

            emailDefaultAddress: null,
            emailDefaultName: record.emailDefaultName,

            title: record.title,
            logo: record.logo,
            background: record.background
        };
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {

            switch (name) {
                case "name":
                    state.record.title = v;
                    state.validate.name = v != "";
                    break;
                case "logo":
                    if (v == null) state.record.logo = null;
                    else state.record.logo = { fileId: v.fileId, url: v.tempUrl };
                    break;
                case "background":
                    if (v == null) state.record.background = null;
                    else state.record.background = { fileId: v.fileId, url: v.tempUrl };
                    break;
                case "thumbLogoUploading":
                    state.validate.thumbLogo = !v;
                    break;
                case "thumbBackUploading":
                    state.validate.thumbBack = !v;
                    break;
                case "minPasswordLen":
                    state.record.minPasswordLen = v;
                    state.validate.minPasswordLen = v >= 5 && v <= 10;
                    break;
                case "maxAuditLogDays":
                    state.record.maxAuditLogDays = v;
                    state.validate.maxAuditLogDays = v >= 0 && v <= 180;
                    break;
                //case "emailDefaultAddress":
                //    state.record.emailDefaultAddress = v;
                //    state.validate.emailDefaultAddress = validateEmail(v);
                //    break;
                default:
                    state.record[name] = v;
                    break;
            }

            return state;
        }, () => this.props.onEditing(this.isRecordChanged()));
    }

    ///
    private formIsValid() {
        return LibUtils.isAllPropertiesWithValue(this.state.validate, true);
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.formIsValid())
            return;

        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveSetupAdmin(dto)
            .then((response) => {
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.props.t("Common.Literals.UpdateSuccess"), "success");
                Session.appTheme = { title: dto.title, themeName: dto.mainTheme, lastUpdateDate: new Date() };

                this.setState({ formCycle: RequestLifeCycle.PostSuccess }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleSelectedTheme = (theme: string) => {
        this.updateRecord("mainTheme", theme);
        this.setState({ dialog: null });
    };

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.compareDto);
    }

    ///
    private buildThemeBox(theme: string, title: string) {

        var t = appMainTheme(theme, Session.locale);
        return (
            <div style={{ display: "block" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ width: 48, height: 48, backgroundColor: t.palette.primary.dark, marginRight: 8 }}></div>
                    <div>{title}</div>
                </div>
            </div>
        );
    }

    ///
    private themeSelector() {
        this.setState({
            dialog: (
                <Dialog open fullWidth maxWidth="sm">
                    <MuiDialogTitleClose
                        onClose={
                            () => {
                                this.setState({ dialog: null });
                            }
                        }
                    >
                        <span>Selecione um tema</span>
                    </MuiDialogTitleClose>
                    <DialogContent>
                        <List>
                            {
                                Object
                                    .keys(appThemes)
                                    .map(
                                        (theme) => (
                                            <ListItem dense button onClick={() => this.handleSelectedTheme(theme)}>
                                                {this.buildThemeBox(theme, appThemeNames[theme][Session.locale])}
                                            </ListItem>
                                        )
                                    )
                            }
                        </List>
                    </DialogContent>
                </Dialog>
            )
        });
    }

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}  >
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        {
                            this.isRecordChanged() &&
                            <ButtonCancel
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.load()}
                            />
                        }
                        <ButtonSave
                            disabled={!this.formIsValid()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />

                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        hideCreator={true}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <SetupAdminLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormMain() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("CustomizationTitle")} />
                    <CardContent>

                        <Grid container spacing={2}>

                            <Grid item xs={12}>
                                <TextField
                                    name="name"
                                    label={this.res("NameLabel")}
                                    autoComplete="off"
                                    error={validate.name === false}
                                    margin="normal"
                                    value={record.title || ""}
                                    onChange={this.handleChange}
                                    inputProps={{ maxLength: 50 }}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <ImageSelection
                                    dimensions={{ width: 256, height: 128 }}
                                    acceptedFiles={MediaUtils.AcceptImages()}
                                    allowEditSelectedImage={true}
                                    boxHeight={130}
                                    selectButtomText={this.res("SelectLogoLabel")}
                                    currentImageUrl={record.logo ? record.logo.url : null}
                                    emptyImageUrl={AppImages.DefaultLogo}
                                    enableClear={true}
                                    variant="boxed"
                                    onClear={() => this.updateRecord("logo", null)}
                                    onFileUploadStarted={() => this.updateRecord("thumbLogoUploading", true)}
                                    onFileUploadFinished={() => this.updateRecord("thumbLogoUploading", false)}
                                    onFileUploadSuccess={(data) => this.updateRecord("logo", data.mainFile)}
                                />
                                <Box mt={1}>
                                    <Alert severity="info">{this.res("RecommendedLogoSize")}:<br />256 x 128 pixels</Alert>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <ImageSelection
                                    acceptedFiles={MediaUtils.AcceptImages()}
                                    allowEditSelectedImage={false}
                                    boxHeight={130}
                                    dimensions={null}
                                    selectButtomText={this.res("SelectLoginImageLabel")}
                                    currentImageUrl={record.background ? record.background.url : null}
                                    emptyImageUrl={AppImages.DefaultBackground}
                                    enableClear={true}
                                    variant="boxed"
                                    onClear={() => this.updateRecord("background", null)}
                                    onFileUploadStarted={() => this.updateRecord("thumbBackUploading", true)}
                                    onFileUploadFinished={() => this.updateRecord("thumbBackUploading", false)}
                                    onFileUploadSuccess={(data) => this.updateRecord("background", data.mainFile)}
                                />
                                <Box mt={1}>
                                    <Alert severity="info">{this.res("RecommendedBgSize")}</Alert>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <MuiClickable onClick={() => { this.themeSelector(); }} >
                                    {this.buildThemeBox(record.mainTheme, appThemeNames[record.mainTheme][Session.locale])}
                                </MuiClickable>
                            </Grid>
                        </Grid>

                    </CardContent>
                </Card>

            </MuiContainer>
        );
    }

    ///
    private renderFormAccount() {
        const { record } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("UserAccountTitle")} />
                    <CardContent>

                        <FormGroup>

                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enableAutoRegister"
                                        checked={record.enableAutoRegister === true}
                                        onChange={this.handleChange}
                                        value="enableAutoRegister"
                                        color="secondary"
                                    />
                                }
                                label={this.res("AutoRegisterLabel")}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        name="requireAccountConfirmation"
                                        checked={record.requireAccountConfirmation === true}
                                        onChange={this.handleChange}
                                        value="requireAccountConfirmation"
                                        color="secondary"
                                    />
                                }
                                label={this.res("AccountConfirmationLabel")}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enablePasswordRecovery"
                                        checked={record.enablePasswordRecovery === true}
                                        onChange={this.handleChange}
                                        value="enablePasswordRecovery"
                                        color="secondary"
                                    />
                                }
                                label={this.res("PasswordRecoveryLabel")}
                            />


                            <FormControlLabel
                                control={
                                    <Switch
                                        name="corporateMode"
                                        checked={record.corporateMode === true}
                                        onChange={this.handleChange}
                                        value="corporateMode"
                                        color="secondary"
                                    />
                                }
                                label={this.res("CorporateModeLabel")}
                            />

                        </FormGroup>

                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    private renderFormPassword() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("AuthenticationTitle")} />
                    <CardContent>

                        <MuiContainer margin="normal">

                            <FormSubtitle text={this.res("PasswordSubtitle")} />

                            <FormGroup>
                                <TextFieldNumeric
                                    autoComplete="false"
                                    name="minPasswordLen"
                                    label={this.res("PasswordLengthLabel")}
                                    error={validate.minPasswordLen === false}
                                    margin="normal"
                                    value={record.minPasswordLen || 5}
                                    onChange={this.handleChange}
                                    allowNegative={false}
                                    maxLength={2}
                                    inputWidth={150}
                                    required
                                />
                            </FormGroup>

                            <FormGroup >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="passwordHasOneNumber"
                                            checked={record.passwordHasOneNumber}
                                            onChange={this.handleChange}
                                            value="passwordHasOneNumber"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("PasswordNumberLabel")}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="passwordHasOneUpperLetter"
                                            checked={record.passwordHasOneUpperLetter}
                                            onChange={this.handleChange}
                                            value="passwordHasOneUpperLetter"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("PasswordLetterLabel")}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="passwordHasOneSymbol"
                                            checked={record.passwordHasOneSymbol}
                                            onChange={this.handleChange}
                                            value="passwordHasOneSymbol"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("PasswordSymbolLabel")}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="passwordExcludeNameSurname"
                                            checked={record.passwordExcludeNameSurname}
                                            onChange={this.handleChange}
                                            value="passwordExcludeNameSurname"
                                            color="secondary"
                                        />
                                    }
                                    label={this.res("ExcludeSurnameLabel")}
                                />

                            </FormGroup>
                        </MuiContainer>

                        <MuiContainer margin="normal">

                            <FormSubtitle text={this.res("AccessSubtitle")} />

                            <FormGroup>
                                <TextFieldNumeric
                                    name="maxInvalidPasswordAttempts"
                                    label={this.res("InvalidPasswordAttemptLabel")}
                                    margin="normal"
                                    value={record.maxInvalidPasswordAttempts || 0}
                                    onChange={this.handleChange}
                                    allowNegative={false}
                                    maxLength={2}
                                    inputWidth={150}
                                    required
                                />
                                <FormHelperText>{this.res("InvalidPasswordAttemptL1")}</FormHelperText>
                                <FormHelperText>
                                    <Trans
                                        i18nKey="Views.System.Components.SetupAdminEdit.InvalidPasswordAttemptL2"
                                        components={[<u></u>]}
                                    />
                                </FormHelperText>
                            </FormGroup>

                            <FormGroup>
                                <TextFieldNumeric
                                    name="passwordAttemptWindow"
                                    label={this.res("AttemptWindowLabel")}
                                    margin="normal"
                                    value={record.passwordAttemptWindow || 0}
                                    onChange={this.handleChange}
                                    allowNegative={false}
                                    maxLength={2}
                                    inputWidth={150}
                                    required
                                />
                                <FormHelperText>{this.res("AttemptWindowL1")}</FormHelperText>
                                <FormHelperText>{this.res("AttemptWindowL2")}</FormHelperText>
                            </FormGroup>

                        </MuiContainer>

                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    private renderFormEmail() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("EmailTitle")} />
                    <CardContent>
                        <FormGroup>
                            <TextField
                                name="emailDefaultName"
                                label={this.res("EmailNameLabel")}
                                margin="normal"
                                value={record.emailDefaultName || ""}
                                onChange={this.handleChange}
                                inputProps={{ maxLength: 50 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PermIdentity />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormGroup>

                        <FormGroup>
                            <TextField
                                disabled
                                name="emailDefaultAddress"
                                label={this.res("EmailLabel")}
                                margin="normal"
                                value={Session.defaultEmailAddress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Alert severity="warning">
                                <Trans
                                    i18nKey="Views.System.Components.SetupAdminEdit.SmtpWarning"
                                    components={[<br />, <b></b>]}
                                />
                            </Alert>
                        </FormGroup>
                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    private renderFormAudit() {

        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("HistoricTitle")} />
                    <CardContent>
                        <FormGroup>
                            <TextFieldNumeric
                                name="maxAuditLogDays"
                                label={this.res("AuditDaysLabel")}
                                error={validate.maxAuditLogDays === false}
                                margin="normal"
                                value={record.maxAuditLogDays || ""}
                                onChange={this.handleChange}
                                maxLength={3}
                                inputWidth={150}
                                required
                            />
                        </FormGroup>
                    </CardContent>
                </Card>
            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {

        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs={12} md={10} lg={8}>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    <RequestErrorDialog
                        open={this.state.formCycle == RequestLifeCycle.PostFailed}
                        error={this.state.lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />

                    {this.state.dialog}

                    {this.renderToolbar()}

                    <form noValidate autoComplete="nope" >
                        {this.renderFormMain()}
                        {this.renderFormAccount()}
                        {this.renderFormPassword()}
                        {this.renderFormEmail()}
                        {this.renderFormAudit()}
                    </form>

                    {this.renderRecordInfo()}

                    <Prompt
                        when={this.isRecordChanged()}
                        message={this.props.t("Common.Question.Unsave")}
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(SetupAdminEdit)));